import { useState } from 'react';
import { EmailMessage } from '../../../../../models/Email';
import EmailHeaderContainer from './EmailHeaderContainer';
import TransferredBanner from './TransferredBanner';
import { useProcessedEmailHtml } from '../../../hooks/useProcessedEmailHtml';
import ReceivedEmailAttachmentChips from './ReceivedEmailAttachmentChips';
import { Alert, Box } from '@coolblue-development/freddy';

// Either the parent owns the collapsed state (thread-level expand/collapse all) and must handle
// the toggle too, or the item manages its own state. Passing only one of the two is a type error.
type CollapseControl =
  | { isCollapsed: boolean; onToggleCollapsed: () => void }
  | { isCollapsed?: undefined; onToggleCollapsed?: undefined };

type EmailMessageItemProps = {
  message: EmailMessage;
  index: number;
  initiallyCollapsed?: boolean;
  onAttachmentDownloadError?: () => void;
} & CollapseControl;

const EmailMessageItem = ({
  message,
  index,
  initiallyCollapsed = true,
  isCollapsed: controlledIsCollapsed,
  onToggleCollapsed,
  onAttachmentDownloadError
}: EmailMessageItemProps): JSX.Element => {
  const [localIsCollapsed, setLocalIsCollapsed] = useState(initiallyCollapsed);
  const isCollapsed = controlledIsCollapsed ?? localIsCollapsed;
  const toggleCollapsed = (): void =>
    onToggleCollapsed ? onToggleCollapsed() : setLocalIsCollapsed(!localIsCollapsed);
  const getProcessedEmailHtml = useProcessedEmailHtml();
  const { attachments, hasRejectedAttachments, contactId, bodyHtml, transferInfo } = message;
  const hasAttachments = attachments && attachments.length > 0;

  return (
    <Box sx={{ maxWidth: '100%', minWidth: 0, overflowX: 'hidden', overflowWrap: 'anywhere' }}>
      {transferInfo && <TransferredBanner transferDetails={transferInfo} />}
      <EmailHeaderContainer message={message} onClick={toggleCollapsed} isCollapsed={isCollapsed} />

      {!isCollapsed && (
        <Box
          id={`email-body-${index}`}
          sx={{
            marginBottom: '16px',
            marginLeft: '16px',
            marginRight: '16px',
            borderRight: '1px solid #DDDDDD',
            borderLeft: '1px solid #DDDDDD',
            borderBottom: '1px solid #DDDDDD',
            borderRadius: '0 0 4px 4px',
            padding: '12px',
            maxHeight: '400px',
            overflow: 'auto',
            minWidth: 0,
            '& img': { maxWidth: '100%' },
            '& table': { maxWidth: '100%' }
          }}
        >
          {hasRejectedAttachments ? (
            <Alert
              description="One or more attachments couldn't be opened."
              intent="error"
              sx={{ marginBottom: '12px' }}
            />
          ) : (
            hasAttachments && (
              <ReceivedEmailAttachmentChips
                attachments={attachments}
                contactId={contactId}
                onDownloadError={onAttachmentDownloadError}
              />
            )
          )}
          <Box dangerouslySetInnerHTML={{ __html: getProcessedEmailHtml(bodyHtml || '<em>No content</em>') }} />
        </Box>
      )}
    </Box>
  );
};

export default EmailMessageItem;
